import type {CommitmentJobDisplay} from './commitment-job-display'
import {DRIVER_SEND_STATUS, driversToNotifyLabel} from './driver-send-status'
import type {OperationalState} from './operational-state'

export type OperationalSignalTier = 'informational' | 'attention' | 'intervention'

export type RadioStatusOptions = {
    warnings?: string[]
    /** Rentals put on rent automatically that dispatch has not confirmed went out. */
    unconfirmedRentalCount?: number
}

export type RadioStatusResult = {
    line: string
    tier: OperationalSignalTier
}

function warningMatches(warnings: string[], pattern: RegExp): boolean {
    return warnings.some((warning) => pattern.test(warning))
}

function shortageLine(
    shortages: string[],
    unnotifiedDriverCount: number,
    onlyDrivers: boolean,
): string {
    const summary = shortages.map((shortage) => shortage.replace(/^Missing\s+/i, '')).join(' · ')
    const notification =
        unnotifiedDriverCount > 0
            ? ` + ${driversToNotifyLabel(unnotifiedDriverCount, onlyDrivers)}`
            : ''
    return `Missing ${summary}${notification}`
}

/** Primary status copy based only on allocation and persisted notification work. */
export function resolveRadioStatus(
    display: CommitmentJobDisplay,
    operational: OperationalState,
    options: RadioStatusOptions = {},
): RadioStatusResult {
    const warnings = options.warnings ?? []

    if (display.shortageSummaryStrings.length > 0) {
        return {
            line: shortageLine(
                display.shortageSummaryStrings,
                display.unnotifiedDriverCount,
                display.unnotifiedOnlyDrivers,
            ),
            tier: 'intervention',
        }
    }

    if (warningMatches(warnings, /fail|undeliver|bounce/i)) {
        return {line: 'Notification failed', tier: 'intervention'}
    }

    if (warningMatches(warnings, /declin/i)) {
        return {line: 'Driver declined', tier: 'intervention'}
    }

    if (warningMatches(warnings, /site|closed|block/i)) {
        return {line: 'Site closed', tier: 'intervention'}
    }

    if (display.unnotifiedDriverCount > 0) {
        return {
            line: driversToNotifyLabel(
                display.unnotifiedDriverCount,
                display.unnotifiedOnlyDrivers,
            ),
            tier: 'intervention',
        }
    }

    if (operational.nextAction === 'assignment') {
        return {line: 'Assign crew', tier: 'intervention'}
    }

    if (operational.nextAction === 'notification') {
        return {line: DRIVER_SEND_STATUS.notifyDrivers, tier: 'intervention'}
    }

    if ((options.unconfirmedRentalCount ?? 0) > 0) {
        return {line: 'Confirm equipment out', tier: 'intervention'}
    }

    if (operational.confidence === 'committed') {
        return {line: 'Dispatched', tier: 'informational'}
    }

    return {line: 'On track', tier: 'informational'}
}

export function radioStatusLine(
    display: CommitmentJobDisplay,
    operational: OperationalState,
    options: RadioStatusOptions = {},
): string {
    return resolveRadioStatus(display, operational, options).line
}

export function radioSecondaryLine(display: CommitmentJobDisplay): string | undefined {
    const resourcePart = display.assignedNames.length > 0 ? display.assignedNames.join(', ') : '—'
    if (!display.customer) {
        return resourcePart === '—' ? undefined : resourcePart
    }
    return `${resourcePart} · ${display.customer}`
}

export function compactJobCode(jobCode: string): string {
    return jobCode.replace(/^JOB-/, '')
}
